/**
 * Ends long-running intents from the run-store (P2 design §4.6): when a run goes from
 * preprocessing / running to completed, errored or cancelled — however this tab learned it
 * (its stream, a resume after a reload, a recovered result, or a realtime hint once
 * `reconcile`'s read-through confirmed it) — the intent bound to its trace (or attached to its
 * client id in this tab) ends with that outcome. The emitter (lib/telemetry/intentEmitter.js)
 * claims the end once across tabs.
 *
 * Only transitions count, with one exception: a run already finished when the watcher starts
 * ends only an intent this tab attached to it before the first answer. Any other finished run
 * was ended by the tab that saw it finish, or ends when a tab resumes it.
 * Runs are compared by object identity, so a dispatch that left a run unchanged costs one
 * comparison per run.
 */
import { STATUS } from "../lib/forecast/model";
import { intentEmitter } from "../lib/telemetry/intentEmitter.js";

const OUTCOME_OF = {
  [STATUS.COMPLETED]: "ok",
  [STATUS.ERRORED]: "error",
  [STATUS.CANCELLED]: "cancelled",
};

const isLive = (status) => status === STATUS.PREPROCESSING || status === STATUS.RUNNING;

/** Watch `store` (the run-store); `endRun` defaults to the emitter's. Returns `stop()`. */
export function watchRunIntents({
  store,
  endRun = (ids, outcome) => intentEmitter().endRun(ids, outcome),
} = {}) {
  if (!store || typeof store.subscribe !== "function") return () => {};
  /** runId → the run object last seen, and whether it was live then. */
  let seen = new Map();
  let lastRuns = null;

  function scan(initial = false) {
    let runs;
    try {
      runs = store.getState()?.runsById;
    } catch {
      return;
    }
    if (!runs || runs === lastRuns) return;
    lastRuns = runs;
    const next = new Map();
    for (const [runId, run] of Object.entries(runs)) {
      const prev = seen.get(runId);
      if (prev && prev.run === run) {
        next.set(runId, prev);
        continue;
      }
      const live = isLive(run?.status);
      next.set(runId, { run, live });
      const outcome = OUTCOME_OF[run?.status];
      // At start, a run that already finished ends only an intent this tab attached to it in
      // memory (one opened before the first /api/me answer): no trace, so no other tab's run.
      if (initial && outcome) {
        try {
          endRun({ traceId: null, runId }, outcome);
        } catch {
          /* never surface */
        }
        continue;
      }
      if (prev?.live && outcome) {
        try {
          endRun({ traceId: run.traceId ?? null, runId }, outcome);
        } catch {
          /* never surface */
        }
      }
    }
    seen = next;
  }

  scan(true);
  const unsubscribe = store.subscribe(() => scan());
  return () => {
    try {
      unsubscribe?.();
    } catch {
      /* ignore */
    }
  };
}
